import { Features } from '@finn/ua-featureflags';
import {
  config,
  generateLocalizationHeaders,
  generateRequestId,
  isServer,
  REQUEST_ID_HEADER_NAME,
} from '@finn/ui-utils/ssr';
import Cookies from 'js-cookie';
import queryString from 'query-string';

import { FilterValuesObject, PRICING_TYPE } from '../helpers/filters';
import { getClosedUserGroupId } from '../helpers/utils';
import { type ProductGroupBy } from '../hooks/useProductListItems';
import { DiscoveryConfigContextType } from './MetadataProvider';

export type GetVehiclesParams = {
  locale: string;
  filters?: FilterValuesObject;
  offset?: number;
  limit?: number;
  groupBy?: ProductGroupBy;
};

const DEFAULT_REQUEST_TIMEOUT = 30000;

const generateRequest = (locale: string) => {
  const requestId = generateRequestId();

  return {
    signal: AbortSignal?.timeout?.(DEFAULT_REQUEST_TIMEOUT),
    headers: {
      ...generateLocalizationHeaders(locale),
      'x-finn-actor': isServer() ? config.UA_FE_SSR_ACTOR : config.UA_FE_ACTOR,
      'Content-Type': 'application/json',
      [REQUEST_ID_HEADER_NAME]: requestId,
    },
  };
};

// small hack for b2b, ideally we should resolve this on config level TODO
const getHost = ({
  apiConfig,
}: {
  apiConfig: DiscoveryConfigContextType['apiConfig'];
}) => {
  if (apiConfig?.products?.startsWith('/api')) {
    return window.location.origin;
  }

  return config.PRODUCT_API_URL;
};

export const fetchProducts = async (
  { apiConfig }: { apiConfig: DiscoveryConfigContextType['apiConfig'] },
  { filters = {}, offset, limit, locale, groupBy }: GetVehiclesParams
) => {
  const hideDownpayment =
    !isServer() && Cookies.get(`${locale}_${Features.HideDownPayment}`) === 'b';
  const cugId = getClosedUserGroupId();
  const pricingType = hideDownpayment
    ? PRICING_TYPE.NORMAL
    : PRICING_TYPE.DOWNPAYMENT;

  const query = {
    pricing_type: pricingType,
    ...filters,
    offset,
    limit,
    cug_id: cugId,
    hide_related: true,
    group_by: groupBy,
  };

  const res = await fetch(
    `${getHost({ apiConfig })}/${apiConfig.products}?${queryString.stringify(query)}`,
    {
      method: 'GET',
      ...generateRequest(locale),
    }
  );
  const json = await res.json();

  return {
    offset: json?.offset ?? 0,
    total_results: json?.total_results,
    results: json?.results,
  };
};

export const fetchProduct = async (
  { apiConfig }: { apiConfig: DiscoveryConfigContextType['apiConfig'] },
  {
    vehicleId,
    locale,
    query,
  }: {
    vehicleId: string;
    locale: string;
    query?: Record<string, string>;
  }
) => {
  const cugId = getClosedUserGroupId();
  const queryStr = queryString.stringify({
    cug_id: cugId || null,
    hide_related: true,
    ...query,
  });

  const res = await fetch(
    `${getHost({ apiConfig })}${apiConfig.products}/${encodeURIComponent(vehicleId)}${queryStr ? `?${queryStr}` : ''}`,
    {
      method: 'GET',
      ...generateRequest(locale),
    }
  );
  const json = await res.json();

  return res.ok ? json : null;
};

export const fetchFilters = async (
  { apiConfig }: { apiConfig: DiscoveryConfigContextType['apiConfig'] },
  {
    filters,
    zipCode,
    pricingType,
    cugId,
    locale,
  }: {
    filters: FilterValuesObject;
    zipCode?: string;
    cugId?: string;
    pricingType?: string;
    locale?: string;
  }
) => {
  const queryStr = queryString.stringify({
    ...filters,
    zipcode: zipCode,
    pricing_type: pricingType,
    cug_id: cugId,
  });

  const res = await fetch(
    `${getHost({ apiConfig })}${apiConfig.filters}${queryStr ? `?${queryStr}` : ''}`,
    {
      method: 'GET',
      ...generateRequest(locale),
    }
  );
  const json = await res.json();

  return json;
};
